import { Box, Center, Divider, Flex, Text } from "@mantine/core";
import { useLocation, useNavigate } from "react-router-dom";
import { type Dispatch, type ReactNode, type Ref, type RefObject, type SetStateAction } from "react";
import { type CompanyWithUI } from "@/components/shared/CompanyCardHorizontal";
import DiscoverCompaniesTable from "@/pages/discover/components/DiscoverCompaniesTable";
import AppPaginationBar from "@/components/shared/AppPaginationBar";
import { successNotification } from "@/shared/utils/successNotification";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { addGuestShortlistCompanyId, removeGuestShortlistCompanyId } from "@/shared/utils/guestShortlistStorage";
import { navigateToCompanyWorkspace } from "@/shared/utils/collaborationNavigation.util";
import { expoCompanyPathFromLegacySearch } from "@/pages/discover/expoGuestConnect.util";
import { COMPACT_COMPANY_GRID_COLS } from "@/pages/discover/DiscoverPage.constants";
import { DiscoverCompanyCardsShimmer } from "@/pages/discover/components/DiscoverPageSkeleton";

type DiscoverResultsSectionProps = {
  companies: CompanyWithUI[];
  selectedSectorTitle: string | null;
  debouncedSearch: string;
  isAuthenticated: boolean;
  /** When provided, used for logged-in "view company" navigation instead of workspaces. */
  loggedInProfilePathBuilder?: (companyId: string) => string;
  loading: boolean;
  showResultsShimmer: boolean;
  shimmerCount?: number;
  matchCount: number | null;
  loadError: string | null;
  isHomeRoute: boolean;
  totalPages: number;
  page: number;
  effectivePageSize: number;
  pageSizeOptions: readonly number[];
  onPageChange: (nextPage: number) => void;
  onPageSizeChange?: (nextPageSize: number) => void;
  addToShortlist: (companyId: string) => Promise<void>;
  removeFromShortlist: (companyId: string) => Promise<void>;
  setCompanies: Dispatch<SetStateAction<CompanyWithUI[]>>;
  setGuestShortlistedIds: Dispatch<SetStateAction<Set<string>>>;
  buildDiscoverProfileSearch: () => string;
  isMyDiscoveryRoute: boolean;
  tableHeaderAnchorRef?: RefObject<HTMLDivElement | null>;
  /** Measured for container-query column count (CTA after first row). */
  gridContainerRef?: Ref<HTMLDivElement | null>;
  canViewCompanyDetails?: boolean;
  onBlockedViewCompanyDetails?: () => void;
  searchRow?: ReactNode;
  showSearchInSection?: boolean;
  /** Compact discover: container-query columns; page size is always 10 (CTA after the first row). */
  isGuestDiscoverGrid?: boolean;
  afterFirstRow?: ReactNode;
  firstRowCount?: number;
};

export function DiscoverResultsSection({
  companies,
  selectedSectorTitle,
  debouncedSearch,
  isAuthenticated,
  loggedInProfilePathBuilder,
  loading,
  showResultsShimmer,
  shimmerCount = 4,
  matchCount,
  loadError,
  isHomeRoute,
  totalPages,
  page,
  effectivePageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  addToShortlist,
  removeFromShortlist,
  setCompanies,
  setGuestShortlistedIds,
  buildDiscoverProfileSearch,
  isMyDiscoveryRoute: _isMyDiscoveryRoute,
  tableHeaderAnchorRef,
  gridContainerRef,
  canViewCompanyDetails = true,
  onBlockedViewCompanyDetails,
  searchRow,
  showSearchInSection = false,
  isGuestDiscoverGrid = false,
  afterFirstRow,
  firstRowCount,
}: DiscoverResultsSectionProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const searchKeyword = debouncedSearch.trim();
  const resultsHeaderKeyword = searchKeyword || null;

  return (
    <>
      <Box ref={tableHeaderAnchorRef}>
        <Flex justify="space-between" align="center" mb="1" gap="sm">
          <Text fw={700} c={searchKeyword ? "slate.6" : "primary.7"}>
            {searchKeyword ? "All Companies" : (selectedSectorTitle ?? "All Companies")}
          </Text>
          {resultsHeaderKeyword ? (
            <Text fw={700} c="primary.7" ta="right" style={{ wordBreak: "break-word" }}>
              {resultsHeaderKeyword}
            </Text>
          ) : null}
        </Flex>
        <Divider color="slate.2" />
      </Box>

      {showSearchInSection && searchRow ? <Box mt="md">{searchRow}</Box> : null}

      <Box
        ref={gridContainerRef}
        data-discover-tour="company-card"
        style={{ position: "relative", minHeight: 200, containerType: "inline-size" }}
        mt="lg"
      >
        {showResultsShimmer ? (
          <DiscoverCompanyCardsShimmer
            count={shimmerCount}
            afterFirstRow={afterFirstRow}
            firstRowCount={firstRowCount}
          />
        ) : companies.length === 0 && !loading ? (
          <Center py="xl">
            <Text c="slate.5"> No results found for your search. Please try again with different filters or keywords.</Text>
          </Center>
        ) : (
          <DiscoverCompaniesTable
            companies={companies}
            gridCols={isGuestDiscoverGrid ? COMPACT_COMPANY_GRID_COLS : undefined}
            compact={isGuestDiscoverGrid}
            afterFirstRow={afterFirstRow}
            firstRowCount={firstRowCount}
            profileSearch={buildDiscoverProfileSearch()}
            discoverReturnTo={(() => {
              const qs = buildDiscoverProfileSearch();
              return qs ? `${location.pathname}${qs}` : location.pathname;
            })()}
            loggedInProfilePathBuilder={loggedInProfilePathBuilder}
            canViewCompanyDetails={canViewCompanyDetails}
            onBlockedViewCompanyDetails={onBlockedViewCompanyDetails}
            onShortlistClick={async (id, isShortlisted) => {
              let previous = false;
              setCompanies((prev) =>
                prev.map((c) => {
                  if (c.id !== id) return c;
                  previous = Boolean(c.isShortlisted);
                  return { ...c, isShortlisted };
                }),
              );

              try {
                if (isShortlisted) {
                  await addToShortlist(id);
                  successNotification({ title: "Success", message: "Company added to shortlist" });
                } else {
                  await removeFromShortlist(id);
                  successNotification({ title: "Success", message: "Company removed from shortlist" });
                }
              } catch (err) {
                handleTrpcError(err);
                setCompanies((prev) => prev.map((c) => (c.id === id ? { ...c, isShortlisted: previous } : c)));
              }
            }}
            onGuestShortlistClick={(id) => {
              const nextIds = addGuestShortlistCompanyId(id);
              const nextSet = new Set(nextIds);
              setGuestShortlistedIds(nextSet);
              setCompanies((prev) => prev.map((c) => (c.id === id ? { ...c, isShortlisted: true } : c)));
            }}
            onGuestShortlistRemove={(id) => {
              const nextIds = removeGuestShortlistCompanyId(id);
              const nextSet = new Set(nextIds);
              setGuestShortlistedIds(nextSet);
              setCompanies((prev) => prev.map((c) => (c.id === id ? { ...c, isShortlisted: false } : c)));
            }}
            onViewDetails={(id) => {
              if (!canViewCompanyDetails) {
                onBlockedViewCompanyDetails?.();
                return;
              }
              const qs = buildDiscoverProfileSearch();
              const returnTo = qs ? `${location.pathname}${qs}` : location.pathname;
              const expoProfilePath = expoCompanyPathFromLegacySearch(id, qs);
              if (expoProfilePath) {
                navigate(expoProfilePath, { state: { discoverReturnTo: returnTo } });
                return;
              }
              if (isAuthenticated) {
                if (loggedInProfilePathBuilder) {
                  navigate(loggedInProfilePathBuilder(id), { state: { discoverReturnTo: returnTo } });
                  return;
                }
                void navigateToCompanyWorkspace(navigate, id, {
                  search: qs,
                  state: { discoverReturnTo: returnTo },
                });
                return;
              }
              navigate(`/discover/${id}${qs}`, { state: { discoverReturnTo: returnTo } });
            }}
          />
        )}
      </Box>

      {loadError && (
        <Center py="md">
          <Text c="red" size="sm">
            {loadError}
          </Text>
        </Center>
      )}

      {(!isHomeRoute || totalPages > 1) && (
        <Box mt="xl">
          <AppPaginationBar
            page={page}
            totalPages={totalPages}
            totalItems={matchCount ?? companies.length}
            pageSize={effectivePageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={[...pageSizeOptions]}
            boundaries={1}
            showTotalItems={false}
            showResultsRange={false}
          />
        </Box>
      )}
    </>
  );
}

export default DiscoverResultsSection;
